import { Progress } from "./ui/progress";
import {
  computeLaborAllocationMetrics,
  formatAllocationProgressPercent,
  formatLaborHoursMetric,
  formatVariancePercent,
} from "../utils/laborAllocationSummary";

function SummaryMetricCard({ title, value, subtitle, valueClassName, loading }) {
  return (
    <article className="rounded-lg border border-gray-200/80 bg-white p-5 shadow-sm dark:border-gray-700/80 dark:bg-gray-900">
      <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{title}</p>
      {loading ? (
        <div className="mt-2 h-9 w-24 animate-pulse rounded bg-gray-200/80 dark:bg-gray-700/80" />
      ) : (
        <p className={`mt-2 text-3xl font-bold tracking-tight ${valueClassName}`}>{value}</p>
      )}
      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{subtitle}</p>
    </article>
  );
}

function varianceValueClassName(varianceTone) {
  switch (varianceTone) {
    case "under":
      return "text-red-600 dark:text-red-400";
    case "over":
      return "text-amber-600 dark:text-amber-400";
    default:
      return "text-emerald-600 dark:text-emerald-400";
  }
}

function allocationProgressBarClassName(allocationProgressPercent) {
  const percent = Number(allocationProgressPercent);
  if (!Number.isFinite(percent) || percent < 80) {
    return "[&_[data-slot=progress-indicator]]:bg-red-500";
  }
  if (percent < 90) {
    return "[&_[data-slot=progress-indicator]]:bg-amber-400";
  }
  return "[&_[data-slot=progress-indicator]]:bg-emerald-500";
}

export function LaborAllocationSummary({
  totalHoursCharged,
  totalHoursAllocated,
  loading = false,
}) {
  const metrics = computeLaborAllocationMetrics({
    totalHoursCharged,
    totalHoursAllocated,
  });

  const progressBarClassName = allocationProgressBarClassName(
    metrics.allocationProgressPercent
  );

  return (
    <div className="mb-6 rounded-lg bg-slate-50 p-5 dark:bg-slate-900/40">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <SummaryMetricCard
          title="Total Hours Charged"
          value={formatLaborHoursMetric(metrics.charged)}
          subtitle="hours this month"
          valueClassName="text-gray-900 dark:text-gray-100"
          loading={loading}
        />
        <SummaryMetricCard
          title="Total Hours Allocated"
          value={formatLaborHoursMetric(metrics.allocated)}
          subtitle="hours allocated to invoices"
          valueClassName="text-blue-600 dark:text-blue-400"
          loading={loading}
        />
        <SummaryMetricCard
          title="Variance"
          value={formatVariancePercent(metrics.variancePercent)}
          subtitle={metrics.varianceSubtitle}
          valueClassName={varianceValueClassName(metrics.varianceTone)}
          loading={loading}
        />
      </div>

      <div className="mt-5">
        <div className="mb-2 flex items-center justify-between gap-4">
          <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Allocation Progress
          </p>
          {loading ? (
            <div className="h-4 w-12 animate-pulse rounded bg-gray-200/80 dark:bg-gray-700/80" />
          ) : (
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {formatAllocationProgressPercent(metrics.allocationProgressPercent)}
            </p>
          )}
        </div>
        {loading ? (
          <div className="h-2 w-full animate-pulse rounded-full bg-gray-200/80 dark:bg-gray-700/80" />
        ) : (
          <Progress
            value={metrics.allocationProgressBarPercent}
            className={`h-2 bg-gray-200 dark:bg-gray-700 ${progressBarClassName}`}
          />
        )}
      </div>
    </div>
  );
}
